// src/components/album/modals/ContentCreationModal.jsx
import React, { useState } from 'react';
import { 
  X, MessageCircle, FileText, HelpCircle, ShoppingCart, Calendar, Users,
  Save, Plus, Trash2, ArrowLeft, Loader2
} from 'lucide-react';
import { flipCardService } from '../../../services/flipCardService';

export const ContentCreationModal = ({ 
  mediaItem,
  bookId,
  galleryId,
  primaryColor,
  onContentCreate,
  onClose 
}) => {
  const [step, setStep] = useState('select'); // 'select' or 'create'
  const [contentType, setContentType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fields: [],
    questions: [],
    actions: []
  });

  const contentTypes = [
    { 
      type: 'form', 
      icon: MessageCircle, 
      label: 'Contact Form', 
      desc: 'Collect information from viewers',
      color: '#10b981'
    },
    { 
      type: 'article', 
      icon: FileText, 
      label: 'Article/Text', 
      desc: 'Add detailed information',
      color: '#3b82f6'
    },
    { 
      type: 'quiz', 
      icon: HelpCircle, 
      label: 'Quiz/Survey', 
      desc: 'Interactive questions',
      color: '#8b5cf6'
    },
    { 
      type: 'product', 
      icon: ShoppingCart, 
      label: 'Product Info', 
      desc: 'Pricing and purchase details',
      color: '#f59e0b'
    },
    { 
      type: 'event', 
      icon: Calendar, 
      label: 'Event Details', 
      desc: 'Date, time, and location',
      color: '#ef4444'
    },
    { 
      type: 'profile', 
      icon: Users, 
      label: 'Profile/Bio', 
      desc: 'Personal or professional info',
      color: '#6366f1'
    }
  ];

  const handleTypeSelect = (type) => {
    setContentType(type);
    setFormData(prev => ({ ...prev, type }));
    setStep('create');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
      placeholder: ''
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const contentData = {
        ...formData,
        mediaItemId: mediaItem.id,
        createdAt: new Date(),
        createdBy: 'current-user-id' // Get from auth context
      };

      // Save to Firestore
      const newContent = await flipCardService.createCardContent(
        bookId,
        galleryId,
        contentData
      );

      // Update the media item to link to this content
      await flipCardService.linkContentToMedia(
        bookId,
        galleryId,
        mediaItem.id,
        newContent.id
      );

      onContentCreate(newContent);
    } catch (error) {
      console.error('Error creating content:', error);
      alert('Failed to create content. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormFields = () => {
    switch (contentType) {
      case 'form':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Form Fields
              </label>
              {formData.fields.map((field, index) => (
                <div key={field.id} className="border border-gray-200 rounded-lg p-3 mb-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Field Label"
                      value={field.label}
                      onChange={(e) => updateField(index, 'label', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <select
                      value={field.type}
                      onChange={(e) => updateField(index, 'type', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="text">Text</option>
                      <option value="email">Email</option>
                      <option value="tel">Phone</option>
                      <option value="textarea">Textarea</option>
                      <option value="select">Dropdown</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(index, 'required', e.target.checked)}
                        className="mr-2"
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addField}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Field
              </button>
            </div>
          </div>
        );

      case 'article':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Article Content
            </label>
            <textarea
              value={formData.content || ''}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="Write your article content here..."
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        );

      case 'product':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price || ''}
                onChange={(e) => handleInputChange('price', e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <select
                value={formData.currency || 'USD'}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="USD">USD</option>
                <option value="ZAR">ZAR</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        );

      case 'event':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Date
              </label>
              <input
                type="datetime-local"
                value={formData.eventDate || ''}
                onChange={(e) => handleInputChange('eventDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="Event location"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {step === 'create' && (
              <button
                onClick={() => setStep('select')}
                className="p-1 text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {step === 'select' ? 'Add Content to Card' : `Create ${contentType.charAt(0).toUpperCase() + contentType.slice(1)}`}
              </h2>
              <p className="text-sm text-gray-600">
                {step === 'select' 
                  ? 'Choose what type of content to add to the back of this card'
                  : 'Fill in the details for your content'
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'select' ? (
            // Content Type Selection
            <div className="grid grid-cols-2 gap-4">
              {contentTypes.map(({ type, icon: Icon, label, desc, color }) => (
                <button
                  key={type}
                  onClick={() => handleTypeSelect(type)}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: color }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-gray-800">{label}</span>
                  </div>
                  <p className="text-sm text-gray-600">{desc}</p>
                </button>
              ))}
            </div>
          ) : (
            // Content Creation Form
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic fields */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Content title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Content-type specific fields */}
              {renderFormFields()}
            </form>
          )}
        </div>

        {/* Footer */}
        {step === 'create' && (
          <div className="flex gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setStep('select')}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.title}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isSubmitting ? 'Creating...' : 'Create Content'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};